import React, {
  useEffect,
  useState,
  useContext,
  useMemo,
  useCallback,
} from "react";
import _omit from "lodash/omit";
import _isEmpty from "lodash/isEmpty";
import _cloneDeep from "lodash/cloneDeep";
import { useDispatch, useSelector } from "react-redux";
import { useToast } from "hooks/useToast";
import { useHistory, useParams } from "react-router-dom";
import { Constants } from "config/constants";
import { getCompanyOfficialsAsync } from "state/slices/tables/companyOfficialsSlice";
import ManageCessationOfficialsTable from "./ManageCessationTable";
import Button from "components/lib/Shared/Button";
import TransactionEditContext from "context/TransactionEditContext";
import { Roles_Permissions_Constants } from "config/roles-permissions";

const CessationComponent = (props) => {
  const { companyId, formFields } = props;
  const dispatch = useDispatch();
  const [officialsList, setOfficialsList] = useState(null);
  const [selectedValueForCessation, setSelectedValueForSession] =
    useState(null);
  const [selectedSubOption, setSelectedSubOption] = useState(null);
  const { toast } = useToast();
  const [changeList, setChangeList] = useState([]);
  const [payload, setPayload] = useState([]);
  const params = useParams();
  const paramsTrxId = params.trxId;

  const [trxId, setTrxId] = useState(null);
  const history = useHistory();
  const { isInTransactionEditContext } = useContext(TransactionEditContext);
  const { initiatedTransactions } = useSelector(
    (state) => state.initiatedTransactionsSlice
  );
  const currentUser = useSelector((state) => state.userSlice);

  const filteredTransactions = useMemo(
    () =>
      initiatedTransactions?.items.filter((transaction) => {
        return (
          transaction.changeType &&
          Object.values(Constants.MAINTAIN_COMPANY_DRAFT_STATUS).includes(
            transaction.status
          )
        );
      }) || [],
    [initiatedTransactions]
  );

  const currentTransaction = useMemo(() => {
    if (paramsTrxId && filteredTransactions.length > 0) {
      return filteredTransactions.find((t) => t._id === paramsTrxId);
    }
    return null;
  }, [paramsTrxId, filteredTransactions]);

  const companyDashboardSlice = useSelector(
    (state) => state.companyDashboardSlice
  );

  const { company, dashboardPermissions, dashboardRoles, designation } =
    useSelector((state) => state.companyDashboardSlice);
  const companyOfficials = companyDashboardSlice.companyOfficials.items;

  const { getCompanyOfficialsStatus } = companyDashboardSlice;
  const { totalCount } = companyDashboardSlice.companyOfficials;

  const designationForAPI = useMemo(() => {
    if (formFields?.designation) {
      return formFields.designation;
    } else if (isInTransactionEditContext && currentTransaction) {
      return currentTransaction.applicationType;
    }
    return formFields.officialMethod;
  }, [
    isInTransactionEditContext,
    currentTransaction,
    formFields.officialMethod,
  ]);

  const hasEditOtherDataPermission = dashboardPermissions.some(
    (permission) =>
      permission.name ===
      Roles_Permissions_Constants.DASHBOARD_PERMISSIONS.EDIT_OTHER_DATA
  );

  const handleChangeOfCessionValue = (e) => {
    const selectedValue = e.target.value;
    setSelectedValueForSession(selectedValue);

    if (selectedValue === "resignation" || selectedValue === "removal") {
      setSelectedSubOption(selectedValue);
    } else {
      setSelectedSubOption(null);
    }
  };

  useEffect(() => {
    if (selectedValueForCessation) {
      const initiateTransactionObj = {
        changeType: formFields.idTypeSelection,
        applicationType: designationForAPI,
        reason: selectedSubOption,
        selectType: selectedValueForCessation,
      };
      window.localStorage.setItem(
        "initiateTransactionObj",
        JSON.stringify(initiateTransactionObj)
      );
    }
  }, [
    selectedSubOption,
    selectedValueForCessation,
    formFields.idTypeSelection,
    designationForAPI,
  ]);

  useEffect(() => {
    if (isInTransactionEditContext && currentTransaction) {
      const { processType } = currentTransaction;

      if (processType === "resignation" || processType === "removal") {
        setSelectedValueForSession(processType);
        setSelectedSubOption(processType);
      } else if (processType === "retirement" || processType === "rotation") {
        setSelectedValueForSession("operation");
        setSelectedSubOption(processType);
      } else if (
        ["death", "bankruptcy", "insanity", "courtOrder"].includes(processType)
      ) {
        setSelectedValueForSession("lackOfCapacity");
        setSelectedSubOption(processType);
      }

      setTrxId(currentTransaction._id);
    }
  }, [isInTransactionEditContext, currentTransaction]);

  const handleSubOptionChange = (e) => {
    const selectedReason = e.target.value;
    setSelectedSubOption(selectedReason);
  };

  const filteredOfficialsList = useMemo(() => {
    if (!officialsList) return [];

    const designationFiltered = officialsList.filter(
      (official) => official.designation === designationForAPI
    );

    if (isInTransactionEditContext && currentTransaction) {
      const allOfficials = (
        currentTransaction.individualOfficials || []
      ).concat(currentTransaction.corporateOfficials || []);
      return designationFiltered.filter(
        (official) =>
          !allOfficials.some((tOfficial) => tOfficial._id === official._id)
      );
    }

    return designationFiltered;
  }, [
    officialsList,
    designationForAPI,
    isInTransactionEditContext,
    currentTransaction,
  ]);

  useEffect(() => {
    if (isInTransactionEditContext && currentTransaction) {
      const allOfficials = (
        currentTransaction.individualOfficials || []
      ).concat(currentTransaction.corporateOfficials || []);
      setChangeList(allOfficials);
    }
  }, [isInTransactionEditContext, currentTransaction]);

  const handleRemoveOfficial = useCallback((officialId) => {
    setChangeList((prev) =>
      prev.filter((official) => official._id !== officialId)
    );
  }, []);

  useEffect(() => {
    if (officialsList === null && getCompanyOfficialsStatus.status !== "idle") {
      toast("error", `Please appoint ${designationForAPI} to proceed further.`);
    }
  }, [officialsList]);

  useEffect(() => {
    setOfficialsList(
      companyOfficials.filter(
        (official) => official?.designation === designationForAPI
      )
    );
  }, [designationForAPI]);

  const submitApplication = async () => {
    if (trxId) {
      history.push(
        `/companies/${companyId}/dashboard/transactions/${trxId}/recent-changes`
      );
    } else {
      toast("error", `no transaction Id`);
    }
  };

  // Define the options as an array for easier mapping
  const cessationOptions = [
    { value: "resignation", label: "Resignation" },
    { value: "removal", label: "Removal" },
    ![
      "Authorized Person",
      "Alternate Director",
      "Company Secretary",
      "Auditor",
      "Administrator",
    ].includes(designationForAPI) && {
      value: "operation",
      label: "Operation of the Articles of Association",
    },
    ![
      "Authorized Person",
      "Alternate Director",
      "Company Secretary",
      "Auditor",
      "Administrator",
    ].includes(designationForAPI) && {
      value: "lackOfCapacity",
      label: "Lack of Capacity",
    },
  ].filter(Boolean);

  useEffect(() => {
    if (!hasEditOtherDataPermission) {
      const value = cessationOptions.filter(
        (option) => option.value === "resignation"
      );
      setSelectedValueForSession(value[0].value);

      if (
        selectedValueForCessation === "resignation" ||
        selectedValueForCessation === "removal"
      ) {
        setSelectedSubOption(selectedValueForCessation);
      } else {
        setSelectedSubOption(null);
      }
    }
  }, [selectedValueForCessation]);

  return (
    <>
      {(officialsList === null || officialsList?.length < 1) && (
        <div className="notification-container flex justify-center px-4 py-10 mt-2 mb-2 bg-gray-200 rounded-sm shadow-sm sm:rounded md:rounded-md relative">
          <h2 className="text-gray-900 font-normal">
            You have no assigned {designationForAPI} to proceed with the
            workflow
          </h2>
        </div>
      )}

      {officialsList?.length > 0 && (
        <>
          <h2 className="font-semibold text-grey-700"> Select Type: </h2>
          <div className="px-4 py-6 mt-2 bg-white rounded-sm shadow-sm sm:rounded md:rounded-md">
            <select
              value={selectedValueForCessation || ""}
              onChange={handleChangeOfCessionValue}
              disabled={
                isInTransactionEditContext || !hasEditOtherDataPermission
              }
              className="w-1/2 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary"
            >
              <option value="">Select a type</option>
              {cessationOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>

            {selectedValueForCessation === "operation" && (
              <div className="mt-4">
                <hr className="border-t border-gray-100 my-4" />
                <h3 className="font-medium text-primary mb-4">
                  Reason from Operation of the Articles of Association:
                </h3>
                <div className="flex space-x-8">
                  {[
                    { value: "retirement", label: "Retirement" },
                    { value: "rotation", label: "Rotation" },
                  ].map((reason) => (
                    <div className="flex items-center" key={reason.value}>
                      <input
                        type="radio"
                        id={reason.value}
                        name="operationReason"
                        value={reason.value}
                        checked={selectedSubOption === reason.value}
                        onChange={handleSubOptionChange}
                        disabled={isInTransactionEditContext}
                        className="w-4 h-4 text-primary border-gray-300 focus:ring-blue-500"
                      />
                      <label
                        htmlFor={reason.value}
                        className="ml-2 text-gray-700"
                      >
                        {reason.label}
                      </label>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {selectedValueForCessation === "lackOfCapacity" && (
              <div className="mt-4">
                <hr className="border-t border-gray-100 my-4" />
                <h3 className="font-medium text-grey-700 mb-4">
                  Reason for Lack of Capacity:
                </h3>
                <div className="flex flex-wrap gap-6">
                  {[
                    { value: "death", label: "Death" },
                    { value: "bankruptcy", label: "Bankruptcy" },
                    { value: "insanity", label: "Mental Incapacity" },
                    { value: "courtOrder", label: "Court Order" },
                  ].map((reason) => (
                    <div className="flex items-center" key={reason.value}>
                      <input
                        type="radio"
                        id={reason.value}
                        name="capacityReason"
                        value={reason.value}
                        checked={selectedSubOption === reason.value}
                        onChange={handleSubOptionChange}
                        disabled={isInTransactionEditContext}
                        className="w-4 h-4 text-primary border-gray-300 focus:ring-blue-500"
                      />
                      <label
                        htmlFor={reason.value}
                        className="ml-2 text-gray-700"
                      >
                        {reason.label}
                      </label>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </>
      )}

      {selectedValueForCessation && (
        <>
          <div className="mt-4">
            {(selectedValueForCessation === "resignation" ||
              selectedValueForCessation === "removal" ||
              (selectedValueForCessation && selectedSubOption)) && (
              <>
                <h4 className="text-gray-800 font-semibold mt-2 mb-2">
                  Officials List
                </h4>
                <div>
                  <ManageCessationOfficialsTable
                    companyId={companyId}
                    data={
                      hasEditOtherDataPermission
                        ? filteredOfficialsList.filter(
                            (official) =>
                              !changeList.some(
                                (changeItem) => official._id === changeItem._id
                              )
                          )
                        : filteredOfficialsList.filter(
                            (official) =>
                              official?.primarySource === currentUser?._id &&
                              !changeList.some(
                                (changeItem) =>
                                  currentUser._id === changeItem.primarySource
                              )
                          )
                    }
                    totalCount={totalCount}
                    currentTab="stakeholderTab"
                    isLoading={getCompanyOfficialsStatus.status === "loading"}
                    selectType={selectedValueForCessation}
                    selectedSubOption={selectedSubOption}
                    setChangeList={setChangeList}
                    setPayload={setPayload}
                    setTrxId={setTrxId}
                    typeOfTable="cessation"
                    isInTransactionEditContext={isInTransactionEditContext}
                    onRemoveOfficial={handleRemoveOfficial}
                    currentTransaction={currentTransaction}
                  />
                </div>
              </>
            )}

            {changeList.length > 0 && (
              <div>
                <h4 className="text-gray-800 font-semibold mt-4 mb-2">
                  Change List
                </h4>
                <div>
                  <ManageCessationOfficialsTable
                    companyId={companyId}
                    data={changeList}
                    currentTransaction={currentTransaction}
                    totalCount={changeList.length}
                    currentTab="stakeholderTab"
                    isLoading={getCompanyOfficialsStatus.status === "loading"}
                    selectType={selectedValueForCessation}
                    selectedSubOption={selectedSubOption}
                    isChangeList={true}
                    onRemoveOfficial={handleRemoveOfficial}
                  />
                  <div>
                    <Button
                      type="submit"
                      className="flex items-center ml-auto px-4 py-2 mb-2 font-semibold text-white rounded bg-sflPrimary mt-2 disabled:cursor-not-allowed"
                      onClick={submitApplication}
                    >
                      Processed
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </>
      )}
    </>
  );
};

export default CessationComponent;
